import { useCallback, useMemo, useState } from 'react';

import {
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { ScrollableTable } from './table/scrollable-table';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { PermissionGate } from '../../hooks/useHasPermission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddPendingInvite } from '@/api/mutations/useAddPendingInvite';
import { useDeletePendingInvite } from '@/api/mutations/useDeletePendingInvite';
import useGetPendingInvites from '@/api/queries/useGetPendingInvites';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { InviteUserDialog } from '@/new/components/invite-user-dialog';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

interface UserTableProps {
  users: User[];
  addedUsers: User[];
  onUserChange: (users: User[]) => void;
  matterCreatorId?: string;
}

export function UserTable({ users, addedUsers, onUserChange, matterCreatorId }: UserTableProps) {
  const { matterId } = useParams();
  if (!matterId) return;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const { user } = useUserContext();

  // Pending invites
  const { data: pendingInvites } = useGetPendingInvites({ matterId });
  const { mutate: addPendingInvite } = useAddPendingInvite({ matterId });
  const { mutate: deletePendingInvite } = useDeletePendingInvite({ matterId });

  const handleRemovePendingInvite = (inviteId: string) => {
    deletePendingInvite({ inviteId });
  };

  // Separate users into added and available
  const { availableUsers } = useMemo(() => {
    const added: User[] = [];
    const available: User[] = [];

    users.forEach((u) => {
      if (addedUsers.some((au) => au.id === u.id) || u.id === matterCreatorId) {
        added.push(u);
      } else {
        available.push(u);
      }
    });

    return { availableUsers: available };
  }, [users, addedUsers, matterCreatorId]);

  const handleAddUser = (user: User) => {
    trackCustomEvent(TrackingEventsV2.MatterAddTeamMemberOnCreator, { userId: user.id });
    onUserChange([...addedUsers, user]);
  };

  // If the entered email already belongs to an org user, add them to the matter directly;
  // otherwise send a pending invite for someone who isn't on Wexler yet.
  const handleInviteOrAddUser = useCallback(
    (email: string) => {
      const existingUser = users.find((u) => u.email.toLowerCase() === email.toLowerCase());
      if (existingUser) {
        if (!addedUsers.some((au) => au.id === existingUser.id)) handleAddUser(existingUser);
        return;
      }
      trackCustomEvent(TrackingEventsV2.MatterInviteUserNoAccount, { userId: user?.id, orgId: user?.organisationId });
      addPendingInvite({ userEmail: email });
    },
    [users, addedUsers, addPendingInvite, user?.id, user?.organisationId],
  );

  const availableTable = useReactTable({
    data: availableUsers,
    columns: [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div
              className={`text-left lowercase ${isDisabled ? 'text-gray-400' : ''}`}
              data-testid={`available-user-email-${row.original.id}`}
            >
              {row.getValue('email')}
              {isDisabled && <span className="ml-2 text-xs text-gray-500">(disabled)</span>}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div className="flex justify-end">
              <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAddUser(row.original)}
                  disabled={isDisabled}
                  data-testid={`add-user-${row.original.id}`}
                >
                  <Icon className="lucide--plus" />
                  Add
                </Button>
              </PermissionGate>
            </div>
          );
        },
      },
    ],
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      globalFilter,
    },
  });

  const tableButtons = (
    <>
      {pendingInvites && pendingInvites.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-700 transition-colors hover:bg-amber-100"
              data-testid="pending-invites-badge"
            >
              Pending Invites ({pendingInvites.length})
              <Icon className="lucide--chevron-down" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96" align="start">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Pending Invites</h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                  {pendingInvites.length}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                These users will be added to the matter when they create their account within 7 days.
              </p>
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-1">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between border-b border-gray-100 bg-white p-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-left text-sm lowercase" data-testid={`pending-invite-email-${invite.id}`}>
                          {invite.userEmail}
                        </span>
                        <span className="text-xs text-gray-400">
                          Invited {new Date(invite.createdDate).toLocaleDateString()}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleRemovePendingInvite(invite.id)}
                        data-testid={`remove-pending-invite-${invite.id}`}
                      >
                        <Icon className="lucide--x" />
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
        <Button data-testid="invite-user-button" variant="primary" onClick={() => setIsInviteDialogOpen(true)}>
          Invite User
          <Icon className="lucide--user-plus" />
        </Button>
      </PermissionGate>

      <InviteUserDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInvite={handleInviteOrAddUser}
      />
    </>
  );

  return (
    <ScrollableTable
      table={availableTable}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      plural="available users"
      additionalButtons={tableButtons}
      className="-mx-8"
    />
  );
}
